/*
 * Maatgids voor de AI-portretten (template product.ai-rider).
 * Markup: snippets/fp-size-guide.liquid. Stijl: component-fp-size-guide.css.
 *
 * - De link zit in variant-selects (wordt door Dawn bij elke maatwissel opnieuw
 *   gerenderd), het paneel erbuiten. Klikken lopen daarom via document-delegatie
 *   op [data-fp-sg-toggle][aria-controls=<paneel-id>].
 * - Maten komen uit de optiewaarden: inch (8" x 12") of cm (20x30 cm).
 * - Open/dicht-staat leeft op dit element (data-open) en overleeft maatwissels
 *   en het verhuizen naar de AI-modal.
 */
if (!customElements.get('fp-size-guide')) {
  // Bekende maten: canonieke inch + cm. 12x18 inch (oude naam) en 30x45 cm
  // wijzen naar dezelfde plek als 12x16 inch / 30x40 cm.
  const FP_SG_SLOTS = [
    { key: '8x12', inch: [8, 12], cm: [20, 30], alt: [] },
    { key: '12x16', inch: [12, 16], cm: [30, 40], alt: [[12, 18], [30, 45]] },
    { key: '16x24', inch: [16, 24], cm: [40, 60], alt: [] },
    { key: '24x36', inch: [24, 36], cm: [60, 90], alt: [] },
  ];
  const FP_SG_SOFA_W = 213; // 84 inch
  const FP_SG_SOFA_H = 88;
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const num = (v) => parseFloat(String(v).replace(',', '.'));
  const same = (a, b) => a[0] === b[0] && a[1] === b[1];

  function fpSgParse(raw) {
    const s = String(raw || '');
    let unit = null;
    let a;
    let b;
    let m = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm/i.exec(s);
    if (m) {
      unit = 'cm';
    } else {
      m = /(\d+(?:\.\d+)?)\s*(?:["”″]|in\b)?\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:["”″]|in\b)/i.exec(s);
      if (m) unit = 'in';
    }
    if (!unit) return null;
    a = num(m[1]);
    b = num(m[2]);
    const slot = FP_SG_SLOTS.find((sl) =>
      same(sl[unit === 'cm' ? 'cm' : 'inch'], [a, b]) || sl.alt.some((x) => same(x, [a, b]))
    );
    let inch;
    let cm;
    if (slot) {
      inch = slot.inch;
      cm = slot.cm;
    } else if (unit === 'in') {
      inch = [a, b];
      cm = [Math.round(a * 2.54), Math.round(b * 2.54)];
    } else {
      cm = [a, b];
      inch = [Math.round(a / 2.54), Math.round(b / 2.54)];
    }
    return { raw: s, unit, inch, cm, key: slot ? slot.key : null };
  }

  const el = (name, attrs, parent) => {
    const node = document.createElementNS(SVG_NS, name);
    Object.keys(attrs || {}).forEach((k) => node.setAttribute(k, attrs[k]));
    if (parent) parent.appendChild(node);
    return node;
  };

  customElements.define(
    'fp-size-guide',
    class FpSizeGuide extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.panelId = `fp-sg-panel-${this.sectionId}`;
        this.panel = this.querySelector('.fp-sg__panel');
        this.scene = this.querySelector('.fp-sg__scene');
        this.labels = this.querySelector('.fp-sg__labels');
        this.sizeLine = this.querySelector('.fp-sg__size');
        this.note = this.querySelector('.fp-sg__note');
        this.items = (this.dataset.values || '')
          .split('|')
          .map(fpSgParse)
          .filter(Boolean)
          .sort((x, y) => x.cm[0] * x.cm[1] - y.cm[0] * y.cm[1]);

        this.onClick = (e) => {
          const t = e.target.closest && e.target.closest('[data-fp-sg-toggle]');
          if (t && t.getAttribute('aria-controls') === this.panelId) this.toggle();
        };
        this.onChange = (e) => {
          const vs = e.target.closest && e.target.closest('variant-selects');
          if (!vs || !vs.id.startsWith(`variant-selects-${this.sectionId}`)) return;
          this.value = e.target.value;
          this.update();
        };
        this.onPreview = () => this.update();
        document.addEventListener('click', this.onClick);
        document.addEventListener('change', this.onChange);
        document.addEventListener('fp-ai:preview', this.onPreview);

        // Dawn publiceert variantChange nadat variant-selects vervangen is.
        try {
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.sync());
          }
        } catch (e) {
          /* geen pubsub */
        }
        // Vangnet: ook als variant-selects op een andere manier wordt vervangen.
        if (this.parentNode) {
          this.observer = new MutationObserver(() => {
            cancelAnimationFrame(this.raf);
            this.raf = requestAnimationFrame(() => this.sync());
          });
          this.observer.observe(this.parentNode, { childList: true });
        }
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onClick);
        document.removeEventListener('change', this.onChange);
        document.removeEventListener('fp-ai:preview', this.onPreview);
        if (this.unsubscribe) this.unsubscribe();
        if (this.observer) this.observer.disconnect();
        cancelAnimationFrame(this.raf);
      }

      get isOpen() {
        return this.dataset.open === 'true';
      }

      toggle() {
        const open = !this.isOpen;
        this.dataset.open = open ? 'true' : 'false';
        this.update();
        if (open) {
          try {
            window.posthog?.capture?.('size_guide_opened', {
              product: this.dataset.handle,
              size: this.value || null,
            });
          } catch (e) {
            /* tracking mag nooit de storefront breken */
          }
        } else {
          const link = document.querySelector(`.fp-sg__link[aria-controls="${this.panelId}"]:not([hidden])`);
          if (link) link.focus();
        }
      }

      // Gekozen optiewaarde uit de (verse) variant-selects lezen.
      sync() {
        const vs =
          document.getElementById(`variant-selects-${this.sectionId}`) ||
          document.querySelector('variant-selects');
        let value = null;
        if (vs) {
          const checked = vs.querySelector('input[type="radio"]:checked');
          const select = vs.querySelector('select');
          value = checked ? checked.value : select ? select.value : null;
        }
        this.value = value;
        this.update();
      }

      get current() {
        return this.items.find((it) => it.raw === this.value) || null;
      }

      update() {
        const current = this.current;
        const usable = !!current;
        this.hidden = !usable;
        const open = this.isOpen && usable;
        this.panel.hidden = !open;
        document.querySelectorAll(`[data-fp-sg-toggle][aria-controls="${this.panelId}"]`).forEach((btn) => {
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (btn.classList.contains('fp-sg__link')) btn.hidden = !usable;
        });
        if (open) this.render(current);
      }

      fillUrl() {
        const gen = this.closest('fp-ai-generator');
        if (gen && gen.modal && gen.modal.open) {
          try {
            if (gen.previewUrl) return gen.previewUrl;
          } catch (e) {
            /* val terug op de productafbeelding */
          }
        }
        return this.dataset.img || '';
      }

      render(current) {
        const items = this.items;
        const cmNative = current.unit === 'cm';
        const gap = 5;
        const rowW = items.reduce((s, it) => s + it.cm[0], 0) + gap * (items.length - 1);
        const maxH = Math.max(...items.map((it) => it.cm[1]));
        const W = Math.max(240, rowW + 24);
        const floorH = 16;
        const above = 20; // cm tussen bank en onderkant canvas
        const margin = 12;
        const H = margin + maxH + above + FP_SG_SOFA_H + (floorH - 6);
        const sb = H - floorH + 6; // onderkant bank (pootjes staan op de vloer)
        const yC = sb - FP_SG_SOFA_H - above; // onderkant canvassen
        const uid = this.sectionId;

        const svg = el('svg', {
          viewBox: `0 0 ${W} ${H}`,
          class: 'fp-sg__svg',
          'aria-hidden': 'true',
          focusable: 'false',
          preserveAspectRatio: 'xMidYMid meet',
        });
        const defs = el('defs', {}, svg);
        const grad = el('linearGradient', { id: `fp-sg-wall-${uid}`, x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
        el('stop', { offset: '0', 'stop-color': '#F6F0E1' }, grad);
        el('stop', { offset: '1', 'stop-color': '#EADFC8' }, grad);
        const sh = el('filter', { id: `fp-sg-shadow-${uid}`, x: '-30%', y: '-30%', width: '160%', height: '170%' }, defs);
        el('feDropShadow', { dx: 0, dy: 1.6, stdDeviation: 2.2, 'flood-color': '#1D2B4E', 'flood-opacity': '0.38' }, sh);

        el('rect', { x: 0, y: 0, width: W, height: H, fill: `url(#fp-sg-wall-${uid})` }, svg);
        el('rect', { x: 0, y: H - floorH, width: W, height: floorH, fill: '#D9C9A8' }, svg);
        el('rect', { x: 0, y: H - floorH, width: W, height: 0.8, fill: '#C9B88F' }, svg);

        // Bank (84 inch / 213 cm breed), plat getekend.
        const sx = (W - FP_SG_SOFA_W) / 2;
        const sofa = el('g', {}, svg);
        el('rect', { x: sx + 14, y: sb - 8, width: 5, height: 8, fill: '#7A5C3E' }, sofa);
        el('rect', { x: sx + FP_SG_SOFA_W - 19, y: sb - 8, width: 5, height: 8, fill: '#7A5C3E' }, sofa);
        el('rect', { x: sx + 18, y: sb - FP_SG_SOFA_H, width: FP_SG_SOFA_W - 36, height: 58, rx: 10, fill: '#9AAABC' }, sofa);
        el('rect', { x: sx + 20, y: sb - 38, width: FP_SG_SOFA_W - 40, height: 30, rx: 8, fill: '#A9B7C6' }, sofa);
        el('rect', { x: sx + FP_SG_SOFA_W / 2 - 0.4, y: sb - 36, width: 0.8, height: 26, fill: '#8C9CB0' }, sofa);
        el('rect', { x: sx, y: sb - 52, width: 26, height: 44, rx: 12, fill: '#8C9CB0' }, sofa);
        el('rect', { x: sx + FP_SG_SOFA_W - 26, y: sb - 52, width: 26, height: 44, rx: 12, fill: '#8C9CB0' }, sofa);

        // Canvassen op ware verhouding, onderkant gelijk.
        let x = (W - rowW) / 2;
        const pos = [];
        items.forEach((it) => {
          const [w, h] = it.cm;
          const y = yC - h;
          const selected = it === current;
          if (selected) {
            el('rect', { x, y, width: w, height: h, fill: '#FFFFFF', filter: `url(#fp-sg-shadow-${uid})` }, svg);
            const url = this.fillUrl();
            if (url) {
              const img = el('image', { x, y, width: w, height: h, preserveAspectRatio: 'xMidYMid slice' }, svg);
              img.setAttribute('href', url);
            }
            el('rect', { x, y, width: w, height: h, fill: 'none', stroke: '#1D2B4E', 'stroke-opacity': '0.35', 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }, svg);
          } else {
            el('rect', {
              x, y, width: w, height: h,
              fill: 'rgba(255,255,255,0.55)',
              stroke: '#8E8268',
              'stroke-width': 1.2,
              'stroke-dasharray': '4 3',
              'vector-effect': 'non-scaling-stroke',
            }, svg);
          }
          pos.push({ it, cx: x + w / 2, selected });
          x += w + gap;
        });

        this.scene.replaceChildren(svg);
        this.scene.style.setProperty('--fp-sg-ratio', `${W} / ${H}`);

        // Labels onder de wand, uitgelijnd op het midden van elk canvas.
        this.labels.replaceChildren(
          ...pos.map(({ it, cx, selected }) => {
            const span = document.createElement('span');
            const dims = cmNative ? it.cm : it.inch;
            span.textContent = `${dims[0]}×${dims[1]}`;
            span.style.left = `${(cx / W) * 100}%`;
            if (selected) span.className = 'is-selected';
            return span;
          })
        );

        const inchTxt = `${current.inch[0]}" × ${current.inch[1]}"`;
        const cmTxt = `${current.cm[0]} × ${current.cm[1]} cm`;
        const [first, second] = cmNative ? [cmTxt, inchTxt] : [inchTxt, cmTxt];
        this.sizeLine.replaceChildren();
        const strong = document.createElement('strong');
        strong.textContent = first;
        this.sizeLine.append(strong, ` · ${second}`);
        this.note.textContent = current.key ? this.getAttribute(`data-note-${current.key}`) || '' : '';
      }
    }
  );
}
